import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import { Box, Button, IconButton, Link } from '@mui/material';
import MediaTypography from 'components/MediaTypography';
import store from 'store';

import MiroSrc from '@common/assets/images/miro.webp';
import { checkRole } from '@common/components/RBAC';
import { useServiceRouter } from '@common/components/ServiceRouter';
import assessmentTypes from '@common/constants/AssessmentTypesIds';
import { getRoute } from '@common/constants/CampusRoutes';
import { IClientGradeBookModel } from '@common/models/ClientGradeBook/ClientGradeBook';

import {
	StyledGradeBookLessonMiroButton,
	StyledGradeBookLessonMiroImage,
} from '../LessonInfo/styled';
import GBLessonStatus from '../Statuses';
import {
	StyledLessonCard,
	StyledLessonGradeCircle,
	StyledLessonGradeItem,
	StyledLessonGrades,
	StyledLessonHeader,
	StyledLessonStatus,
	StyledLessonTeacher,
	StyledLessonTeacherAvatar,
	StyledLessonTitle,
	StyledLessonViewWrap,
} from './styled';

interface PropsType {
	gradeBook: IClientGradeBookModel;
	onBack: () => void;
}

function GradeBookLessonView({ gradeBook, onBack }: PropsType) {
	const { t } = useTranslation();
	const { checkService } = useServiceRouter();

	const isTis = checkService('international');

	const lesson = gradeBook.schedule_lesson;
	const programName = lesson?.schedule_program?.program_group?.program?.official_name;
	const programSubtitle = lesson?.name || lesson?.lesson?.name;
	const lessonName = lesson?.name || lesson?.lesson?.name;
	const statusChip = GBLessonStatus()[gradeBook?.is_present];
	const dateLabel = gradeBook?.gb_date ? gradeBook.dateTime.toFormat('LLLL dd, yyyy') : '';
	const themeText = lesson?.toHomework?.from_schedule_lesson?.theme;
	const zoomLink = lesson?.getZoomLink(store.services.current);
	const homework = lesson?.toHomework;
	const homeworkDescription = homework?.description;
	const homeworkLinks = homework?.file_urls || ([] as string[]);
	const miroLink = gradeBook?.schedule_lesson?.schedule_program?.miro_link;
	const lessonType = lesson?.type;
	const assessmentType = lesson?.lesson?.assessment_type_id;
	const assessmentWeight = Math.round(parseFloat(lesson?.lesson?.weight || '0'));
	const isAasL = assessmentType === 3;
	const grades =
		gradeBook?.grades?.filter(
			(grade) => grade?.grade !== null && grade?.grade !== undefined && grade?.gradeType?.name,
		) || [];

	const gradeItems = useMemo(() => {
		const list: { id: string | number; value: string | number; label: string }[] = [];

		if (gradeBook.final_grade) {
			list.push({
				id: 'final',
				value: isAasL ? 'Completed' : gradeBook.final_grade,
				label: isAasL ? '' : t('gradeBook.finalGrade', { defaultValue: 'Final grade' }),
			});
		}

		grades.forEach((grade) => {
			if (grade.grade) {
				list.push({
					id: grade.id,
					value: grade.grade,
					label: grade.gradeType?.name || '',
				});
			}
		});

		return list;
	}, [gradeBook.final_grade, grades, isAasL, t]);

	const comments = useMemo(() => {
		const isUkrainianExternship = gradeBook.location_id === 10;
		const gradeComments = isUkrainianExternship
			? gradeBook.grades
					.filter((grade) => grade?.chunk?.chunk_type?.id === 5)
					.map((grade) => grade.comment)
					.filter((comment): comment is string => Boolean(comment))
			: [];

		const finalComments = [gradeBook.final_comment, ...gradeComments].filter(
			(comment): comment is string => Boolean(comment),
		);

		const isShownComment =
			Boolean(finalComments.length) && (checkRole('Parent') || gradeBook.is_show_final_comment);

		return {
			text: isShownComment ? finalComments.join('\n') : '',
			teacher: lesson?.teacher?.fullName || '',
		};
	}, [
		gradeBook.final_comment,
		gradeBook.grades,
		gradeBook.is_show_final_comment,
		gradeBook.location_id,
		lesson?.teacher?.fullName,
	]);

	const lessonHref = getRoute(
		['PROGRAMS'],
		gradeBook?.schedule_lesson.schedule_program.program_group.program.id, // programId
		gradeBook?.schedule_lesson.schedule_program.program_group.group_id, // groupId,
		gradeBook?.schedule_lesson.schedule_program.program_group.id, // programGroupId,
		gradeBook?.schedule_lesson?.toHomework?.from_schedule_lesson?.id, // lesson.lesson_id
	);

	return (
		<StyledLessonViewWrap>
			<StyledLessonHeader>
				<IconButton onClick={onBack} size="small">
					<ArrowBackRoundedIcon />
				</IconButton>
				<StyledLessonTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
						<Link href={lessonHref}>
							<MediaTypography
								sx={{
									textDecoration: 'underline',
									textAlign: 'center',
									color: 'rgb(32, 144, 255)',
								}}
								variant="body1"
							>
								{programName || lessonName}
							</MediaTypography>
						</Link>
						{lessonHref && (
							<IconButton LinkComponent="a" href={lessonHref}>
								<ArrowCircleRightOutlinedIcon
									sx={{
										color: 'rgb(32, 144, 255)',
									}}
									fontSize="small"
								/>
							</IconButton>
						)}
					</Box>
					{dateLabel && (
						<MediaTypography textAlign="center" variant="body2" color="#707070">
							{dateLabel}
						</MediaTypography>
					)}
				</StyledLessonTitle>
				{statusChip && (
					<StyledLessonStatus color={statusChip.props.color} label={statusChip.props.label} />
				)}
			</StyledLessonHeader>

			{programSubtitle && (
				<StyledLessonCard>
					<MediaTypography variant="body1" color="#333">
						{programSubtitle}
					</MediaTypography>
				</StyledLessonCard>
			)}

			{themeText && (
				<StyledLessonCard>
					<MediaTypography variant="caption1" color="#5C5C5C">
						{t('homeWork.lessonTopic') || 'Theme'}
					</MediaTypography>
					<MediaTypography variant="body1" color="#333">
						{themeText}
					</MediaTypography>
				</StyledLessonCard>
			)}

			{isTis && (lessonType || assessmentType) && (
				<StyledLessonCard>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
						{lessonType && (
							<Box>
								<MediaTypography variant="caption1" color="#5C5C5C">
									{t('schedule.type', { defaultValue: 'Type' })}
								</MediaTypography>
								<MediaTypography variant="body1" color="#333">
									{lessonType}
								</MediaTypography>
							</Box>
						)}
						{assessmentType && (
							<Box>
								<MediaTypography variant="caption1" color="#5C5C5C">
									{t('gradeBook.assesmentType')}
								</MediaTypography>
								<MediaTypography variant="body1" color="#333">
									{assessmentTypes[assessmentType]}
									{assessmentType === 2 && assessmentWeight ? ` (${assessmentWeight}%)` : ''}
								</MediaTypography>
							</Box>
						)}
					</Box>
				</StyledLessonCard>
			)}

			{gradeItems.length > 0 && (
				<Box>
					<MediaTypography variant="caption1" color="#5C5C5C">
						{t('gradeBook.grades')}
					</MediaTypography>
					<StyledLessonGrades>
						{gradeItems.map((grade) => (
							<StyledLessonGradeItem key={grade.id}>
								<StyledLessonGradeCircle
									sx={
										grade.value === 'Completed'
											? {
													borderRadius: '12px',
													padding: '0 10px',
													minWidth: 'auto',
												}
											: {}
									}
								>
									{grade.value}
								</StyledLessonGradeCircle>
								<MediaTypography variant="body2" color="#1F1F1F">
									{grade.label}
								</MediaTypography>
							</StyledLessonGradeItem>
						))}
					</StyledLessonGrades>
				</Box>
			)}

			{comments.text && (
				<StyledLessonCard>
					<MediaTypography variant="caption1" color="#5C5C5C">
						{t('gradeBook.comment')}
					</MediaTypography>
					<StyledLessonTeacher>
						<StyledLessonTeacherAvatar>
							{comments.teacher ? comments.teacher[0] : 'T'}
						</StyledLessonTeacherAvatar>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
							<MediaTypography variant="body2" color="#1F1F1F">
								{comments.teacher || t('program.teacher')}
							</MediaTypography>
							<MediaTypography variant="caption1" color="#9E9E9E">
								{t('program.teacher')}
							</MediaTypography>
						</Box>
					</StyledLessonTeacher>
					<MediaTypography
						variant="body1"
						color="#333"
						dangerouslySetInnerHTML={{
							__html: comments.text,
						}}
					/>
				</StyledLessonCard>
			)}

			{(homeworkDescription || homeworkLinks.length > 0) && (
				<StyledLessonCard>
					<MediaTypography variant="caption1" color="#5C5C5C">
						{t('homeWork.main')}
					</MediaTypography>
					{homeworkDescription && (
						<MediaTypography
							variant="body1"
							color="#333"
							sx={{
								img: {
									display: 'flex',
									width: '100%',
									objectFit: 'contain',
								},
								figure: {
									margin: 0,
								},
							}}
							dangerouslySetInnerHTML={{
								__html: homeworkDescription,
							}}
						/>
					)}

					{homeworkLinks.length > 0 && (
						<Box
							sx={{
								borderTop: '1px solid #E8F4FF',
								paddingTop: '4px',
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
								width: '100%',
							}}
						>
							{homeworkLinks.map((href, idx) => (
								<Link
									key={`${href}-${idx}`}
									href={href}
									target="_blank"
									sx={{
										display: 'flex',
										alignItems: 'center',
										padding: '4px',
										fontWeight: 400,
										gap: '8px',
										wordBreak: 'break-all',
									}}
								>
									<InsertLinkIcon fontSize="small" />
									{href}
								</Link>
							))}
						</Box>
					)}
				</StyledLessonCard>
			)}

			{miroLink && (
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<MediaTypography variant="caption1" color="#5C5C5C">
						{t('lesson.miroLink')}
					</MediaTypography>
					<Link href={miroLink} target="_blank">
						<StyledGradeBookLessonMiroButton sx={{ width: '100%', minHeight: '48px' }}>
							<StyledGradeBookLessonMiroImage src={MiroSrc} alt="Miro" />
						</StyledGradeBookLessonMiroButton>
					</Link>
				</Box>
			)}

			{zoomLink && (
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<MediaTypography variant="caption1" color="#5C5C5C">
						{t('lesson.zoomLink')}
					</MediaTypography>
					<Button
						variant="outlined"
						LinkComponent="a"
						href={zoomLink}
						target="_blank"
						sx={{ width: '100%' }}
						endIcon={<ArrowOutwardIcon sx={{ fontSize: 16 }} />}
					>
						{t('gradeBook.join')}
					</Button>
				</Box>
			)}
		</StyledLessonViewWrap>
	);
}

export default GradeBookLessonView;
